/**
 * <LoginPasswordStep> — le mot de passe d'une adresse e-mail déjà donnée (organism)
 *
 * L'adresse est connue : on la montre au lieu de la redemander, avec « Modifier » pour revenir.
 * Sous le mot de passe, le code par e-mail reste à un geste — et « Mot de passe oublié ? » y mène
 * aussi : la personne entre avec le code, un nouveau mot de passe se choisit ensuite.
 *
 * Contrôlé : valeurs, erreurs et envois en cours arrivent en props.
 */
import React, { useRef } from "react"
import { type TextInput as RNTextInput } from "react-native"

import { Banner } from "../../atoms/Banner"
import { Button } from "../../atoms/Buttons/Button"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Stack } from "../../atoms/containers/Stack"
import { Divider } from "../../atoms/Divider"
import { TextInput, textInputAutofill } from "../../atoms/TextInput"
import { ErrorText } from "../../atoms/typography/ErrorText"
import { AuthHeading } from "../../molecules/AuthHeading"
import { LoginTerms } from "../../molecules/LoginTerms"
import { ReadOnlyField } from "../../molecules/ReadOnlyField"

export type LoginPasswordStepProps = {
    email: string
    /**
     * Le champ e-mail, quand l'étape s'ouvre sans adresse connue (un lien direct vers le mot de
     * passe). Absent → l'adresse est montrée, pas saisie.
     */
    onEmailChange?: (email: string) => void
    emailError?: string
    /** « Modifier » l'adresse montrée. Absent → l'adresse est figée. */
    onEditEmail?: () => void
    /** « Votre mot de passe vous a été envoyé par email » — l'arrivée depuis une inscription. */
    passwordSentByEmail?: boolean
    password: string
    onPasswordChange: (password: string) => void
    /** Le refus du serveur : identifiants faux, ou trop d'essais. */
    passwordError?: string
    /** La connexion part — « Continuer » tourne. */
    loggingIn?: boolean
    /** Le mot de passe prend le focus à l'ouverture — sur le web, quand l'adresse est déjà connue. */
    autoFocusPassword?: boolean
    onSubmit: () => void
    onForgotPassword: () => void
    onRequestCode: () => void
    /** Le code part — « Recevoir un code par e-mail » tourne. */
    requestingCode?: boolean
    /** Le refus du serveur à l'envoi du code. */
    codeRequestError?: string
    onOpenTerms?: () => void
    testID?: string
}

export function LoginPasswordStep(props: LoginPasswordStepProps) {
    const {
        email,
        onEmailChange,
        emailError,
        onEditEmail,
        passwordSentByEmail = false,
        password,
        onPasswordChange,
        passwordError,
        loggingIn = false,
        autoFocusPassword = false,
        onSubmit,
        onForgotPassword,
        onRequestCode,
        requestingCode = false,
        codeRequestError,
        onOpenTerms,
        testID,
    } = props
    // Le champ e-mail libre passe la main au mot de passe à « Entrée », comme l'ancien écran.
    const passwordRef = useRef<RNTextInput>(null)
    const noEmail = email.trim() === ""
    // Une seule porte pour « Continuer » et pour la touche d'envoi du clavier.
    const canSubmit = password !== "" && !noEmail && !loggingIn
    const submit = () => {
        if (canSubmit) onSubmit()
    }

    return (
        <Stack gap="6" testID={testID}>
            <AuthHeading title="Votre mot de passe" />

            <Stack gap="3">
                {onEmailChange != null ? (
                    <TextInput
                        {...textInputAutofill.email}
                        size="lg"
                        value={email}
                        onChangeText={onEmailChange}
                        placeholder="Adresse e-mail"
                        accessibilityLabel="Adresse e-mail"
                        returnKeyType="next"
                        onSubmitEditing={() => passwordRef.current?.focus()}
                        error={emailError}
                    />
                ) : (
                    <ReadOnlyField label="Adresse e-mail" value={email} onEdit={onEditEmail} />
                )}

                {passwordSentByEmail && (
                    <Banner
                        color="success"
                        icon="info-circle"
                        tinted
                        message="Votre mot de passe vous a été envoyé par email"
                    />
                )}

                <Stack gap="1">
                    <TextInput
                        {...textInputAutofill.currentPassword}
                        ref={passwordRef}
                        size="lg"
                        value={password}
                        onChangeText={onPasswordChange}
                        placeholder="Mot de passe"
                        accessibilityLabel="Mot de passe"
                        autoFocus={autoFocusPassword}
                        onSubmitEditing={submit}
                        error={passwordError}
                    />
                    <Stack align="start">
                        <TextButton label="Mot de passe oublié ?" onPress={onForgotPassword} />
                    </Stack>
                </Stack>

                <Button fullWidth label="Continuer" disabled={!canSubmit} loading={loggingIn} onPress={submit} />
                <Divider label="ou" spacing="2" />
                <Stack gap="2">
                    <Button
                        variant="outline"
                        fullWidth
                        iconName="envelope"
                        label="Recevoir un code par e-mail"
                        disabled={noEmail}
                        loading={requestingCode}
                        onPress={onRequestCode}
                    />
                    {codeRequestError != null && (
                        <ErrorText center live>
                            {codeRequestError}
                        </ErrorText>
                    )}
                </Stack>
            </Stack>

            <LoginTerms onOpenTerms={onOpenTerms} />
        </Stack>
    )
}
